/**
 * src/api/riskApi.js
 *
 * Risk CRUD and governance lifecycle API.
 * Calls the risks-read and risks-write Azure Functions.
 */

import { azureGet, azurePost } from './azureClient.js';

// Strip transient, client-only fields before a risk is sent to the canonical
// endpoint. Any `_`-prefixed top-level key (e.g. `_writeStatus`, set by the
// write outbox) is a per-record UI marker that must never reach the
// tenant_risk_state table (ADR-0001 §4.6 risk 3). Exported for the unit test.
export function stripTransientRiskFields(risk) {
  if (!risk || typeof risk !== 'object') return risk;
  const out = {};
  for (const key of Object.keys(risk)) {
    if (key.charCodeAt(0) === 95) continue; // '_' prefix
    out[key] = risk[key];
  }
  return out;
}

// ── Risk CRUD ────────────────────────────────────────────────────────────────

/**
 * Read the tenant's risk register.
 *
 * Throws when the read fails. It used to catch everything and return `[]`, and
 * both callers write the result straight into `state.risks` — so any failed
 * read (a 409 while a concurrent writer held the row, a 500, a dropped
 * connection) silently replaced the whole register with "there are no risks".
 * That is GH #6600's actual observation: two tabs raced a draft→review
 * submission and two immediate reads disagreed — "In review", then "". The
 * second read had not found a risk in a different state; it had not found a
 * risk at all, because a failure was substituted with an empty collection and
 * displayed as truth.
 *
 * An empty register is a legitimate answer the server gives, so it cannot also
 * be how failure is spelled. Callers keep what they already had and say the
 * refresh did not happen (TRYGGR_INTENT.md §6.2 — the product must never
 * present a state the server did not report).
 */
export async function readRisks(_tenantId) {
  const data = await azureGet('/api/risks-read');
  return data.risks ?? [];
}

/** Read one risk. Throws when the read fails — see readRisks above. */
export async function readRisk(_tenantId, riskId) {
  const data = await azureGet('/api/risks-read', { riskId });
  return data.risk ?? null;
}

export async function createRisk(_tenantId, riskData) {
  const data = await azurePost(
    '/api/risks-write?action=create',
    stripTransientRiskFields(riskData)
  );
  return data.risk;
}

/**
 * Store an edit of one risk. `base` is the risk as the caller last read it —
 * the record the edit was made on. The server applies only what differs from
 * `base` to the risk as stored now, so an edit never reverts a field someone
 * else changed meanwhile, and a clash on the same field is refused with a 409
 * naming it (`err.conflicts`, GH #7935). Required: without it the server
 * cannot tell an edit from a resend, and refuses the request.
 *
 * `keepMine` (§13 d): after such a 409, the user's choice to keep their own
 * values — the refused `conflicts` resent as returned. The server honours each
 * only while the stored value is still the one the user was shown.
 * `keepTheirs`: the user's choice to take the other value instead — the same
 * edit resent from the same base, and the server drops the user's change at
 * exactly those fields while every other field of the edit still lands.
 */
export async function updateRisk(_tenantId, riskData, base, choice = {}) {
  const data = await azurePost(
    '/api/risks-write?action=update',
    updateBody(riskData, base, choice)
  );
  return data.risk;
}

// The request body of an update, shared by updateRisk and a composed save's
// update step so both measure the edit from `base` the same way.
function updateBody(riskData, base, { keepMine, keepTheirs } = {}) {
  if (!base || typeof base !== 'object') {
    throw new TypeError('updateRisk requires the risk as last read (base)');
  }
  // Strip transient `_`-fields (write outbox markers) and `status` before
  // sending — handleUpdate rejects `status` even as a no-op.
  const { status: _status, ...payload } = stripTransientRiskFields(riskData);
  const readAs = stripTransientRiskFields(base);
  const sentBase = {};
  for (const key of Object.keys(payload)) {
    if (key !== 'id' && readAs[key] !== undefined) sentBase[key] = readAs[key];
  }
  const body = { ...payload, base: sentBase };
  if (Array.isArray(keepMine) && keepMine.length) {
    body.keepMine = keepMine.map(({ path, theirs }) => ({ path, theirs }));
  }
  if (Array.isArray(keepTheirs) && keepTheirs.length) {
    body.keepTheirs = keepTheirs.map(({ path }) => ({ path }));
  }
  return body;
}

/**
 * The steps of one composed save (GH #7836), in the order the server runs
 * them: the edit (measured from `base`, with the user's clash choice), each
 * assertion, then the submission. `submit.ifAssessedFrom` lets the server
 * decide on the locked row whether this save is a fresh submission. `review`
 * (instead of `submit`) ends the save with the reviewer's outcome (GH #8697).
 */
export function composeRiskSteps({ edit, base, choice, assertions = [], submit, review } = {}) {
  const steps = [];
  if (edit) {
    const { id: _id, ...body } = updateBody(edit, base, choice);
    steps.push({ action: 'update', ...body });
  }
  for (const a of assertions) {
    steps.push({
      action: 'record-assertion',
      assertion: a.assertion,
      ...(a.expectedCurrentAssertionId !== undefined
        ? { expectedCurrentAssertionId: a.expectedCurrentAssertionId }
        : {}),
    });
  }
  if (submit) {
    steps.push({
      action: 'submit-for-review',
      narrative: submit.narrative ?? null,
      ...(submit.ifAssessedFrom ? { ifAssessedFrom: submit.ifAssessedFrom } : {}),
    });
  }
  if (review) {
    steps.push({
      action: 'review-assessment',
      outcome: review.outcome,
      narrative: review.narrative ?? null,
      ...(review.recommendedResolutionId
        ? { recommendedResolutionId: review.recommendedResolutionId }
        : {}),
    });
  }
  return steps;
}

/**
 * Store a composed save in one request: every step lands, or none does. The
 * answer lists what each step did; a submit the server skipped says why.
 */
export async function saveRiskSteps(_tenantId, { id, steps, expectedState }) {
  const data = await azurePost('/api/risks-write?action=steps', {
    id,
    steps,
    ...expectedStateField(expectedState),
  });
  return {
    risk: data.risk,
    steps: Array.isArray(data.steps) ? data.steps : [],
    assertion: data.assertion,
    fromState: data.fromState,
  };
}

export async function initializeRiskGovernance(_tenantId, id) {
  const data = await azurePost('/api/risks-write?action=initialize-governance', { id });
  return data.risk;
}

export async function approveRisk(
  _tenantId,
  { id, approvedResolutionId, approvedByName, expectedState }
) {
  const data = await azurePost('/api/risks-write?action=approve', {
    id,
    approvedResolutionId,
    approvedByName,
    ...expectedStateField(expectedState),
  });
  return data.risk;
}

export async function resolveRisk(_tenantId, { id, status, expectedState }) {
  const data = await azurePost('/api/risks-write?action=resolve', {
    id,
    status: status || 'resolved',
    ...expectedStateField(expectedState),
  });
  return data.risk;
}

// ── Risk governance lifecycle ─────────────────────────────────────────────────

/**
 * Every transition below takes an optional `expectedState`: the lifecycle
 * state the page showed when the user acted. When the server refuses the
 * transition and the stored state is no longer that one, it answers a 409
 * FIELD_CONFLICT naming "state" (TRYGGR_INTENT §13 d) instead of a bare
 * refusal. Chained steps pass the state the previous call returned.
 */
function expectedStateField(expectedState) {
  return typeof expectedState === 'string' && expectedState ? { expectedState } : {};
}

// An assessment replaces the risk's current one, often without moving its
// state (an amendment stays 'assessed'), so the state alone cannot tell two
// assessors apart: `expectedCurrentAssertionId` is the current assessment the
// page showed (null for none). Omitted → the server compares what it read.
export async function recordRiskAssertion(
  _tenantId,
  { id, assertion, expectedState, expectedCurrentAssertionId }
) {
  const data = await azurePost('/api/risks-write?action=record-assertion', {
    id,
    assertion,
    ...expectedStateField(expectedState),
    ...(expectedCurrentAssertionId !== undefined ? { expectedCurrentAssertionId } : {}),
  });
  return { risk: data.risk, assertion: data.assertion, fromState: data.fromState };
}

export async function submitRiskForReview(_tenantId, { id, narrative, expectedState }) {
  const data = await azurePost('/api/risks-write?action=submit-for-review', {
    id,
    narrative,
    ...expectedStateField(expectedState),
  });
  return data.risk;
}

export async function reviewRiskAssessment(
  _tenantId,
  { id, outcome, narrative, recommendedResolutionId, expectedState }
) {
  const data = await azurePost('/api/risks-write?action=review-assessment', {
    id,
    outcome,
    narrative,
    recommendedResolutionId,
    ...expectedStateField(expectedState),
  });
  return data.risk;
}

export async function recordRiskDecision(_tenantId, { id, decision, expectedState }) {
  const data = await azurePost('/api/risks-write?action=record-decision', {
    id,
    decision,
    ...expectedStateField(expectedState),
  });
  return { risk: data.risk, decision: data.decision };
}

/**
 * Record that a task backs a risk's current decision.
 *
 * The only path by which a task id reaches a decision. Activation used to
 * accept ids in its own payload, which meant any string satisfied RISK-B-006
 * — the risk store cannot resolve a task id, so nothing checked. Called as
 * the risk module creates each task, so the link is written by the product's
 * own path rather than asserted at the moment the gate is due.
 */
export async function linkRiskTask(_tenantId, { id, taskId, decisionId, expectedState }) {
  const data = await azurePost('/api/risks-write?action=link-task', {
    id,
    taskId,
    ...(decisionId ? { decisionId } : {}),
    ...expectedStateField(expectedState),
  });
  return { risk: data.risk, decision: data.decision };
}

export async function activateRiskTreatment(_tenantId, { id, expectedState }) {
  // No task ids here by design — see linkRiskTask above. The server refuses
  // activation when the decision carries neither a linked task nor an active
  // follow-up requirement (§11, RISK-B-006).
  const data = await azurePost('/api/risks-write?action=activate-treatment', {
    id,
    ...expectedStateField(expectedState),
  });
  return { risk: data.risk, decision: data.decision };
}

export async function rejectRiskApproval(_tenantId, { id, narrative, expectedState }) {
  const data = await azurePost('/api/risks-write?action=reject-approval', {
    id,
    narrative,
    ...expectedStateField(expectedState),
  });
  return data.risk;
}

export async function recordRiskEvidence(_tenantId, { id, evidence, expectedState }) {
  const data = await azurePost('/api/risks-write?action=record-evidence', {
    id,
    evidence,
    ...expectedStateField(expectedState),
  });
  return { risk: data.risk, evidence: data.evidence };
}

export async function reopenRisk(_tenantId, { id, reopenReason, expectedState }) {
  const data = await azurePost('/api/risks-write?action=reopen', {
    id,
    reopenReason,
    ...expectedStateField(expectedState),
  });
  return data.risk;
}

export async function retireRisk(_tenantId, { id, reason, expectedState }) {
  const data = await azurePost('/api/risks-write?action=retire', {
    id,
    reason,
    ...expectedStateField(expectedState),
  });
  return data.risk;
}

export async function createRiskObligation(_tenantId, { id, obligation, expectedState }) {
  const data = await azurePost('/api/risks-write?action=create-obligation', {
    id,
    obligation,
    ...expectedStateField(expectedState),
  });
  return { risk: data.risk, obligation: data.obligation };
}

export async function satisfyRiskObligation(
  _tenantId,
  { id, obligationId, narrative, expectedState }
) {
  const data = await azurePost('/api/risks-write?action=satisfy-obligation', {
    id,
    obligationId,
    narrative,
    ...expectedStateField(expectedState),
  });
  return { risk: data.risk, obligation: data.obligation, evidence: data.evidence };
}

export async function deactivateRiskObligation(
  _tenantId,
  { id, obligationId, reason, expectedState }
) {
  const data = await azurePost('/api/risks-write?action=deactivate-obligation', {
    id,
    obligationId,
    reason,
    ...expectedStateField(expectedState),
  });
  return { risk: data.risk, obligation: data.obligation, evidence: data.evidence };
}

export async function forwardRiskApproval(
  _tenantId,
  { id, newApproverId, comment, expectedState, expectedApproverId }
) {
  const data = await azurePost('/api/risks-write?action=forward-approval', {
    id,
    newApproverId,
    comment,
    ...expectedStateField(expectedState),
    // The approver the page showed: forwarding writes approverId, so a move of
    // it since then is a clash the server names (§13 d).
    ...(expectedApproverId !== undefined ? { expectedApproverId } : {}),
  });
  return { risk: data.risk, evidence: data.evidence };
}

export async function addRiskReviewer(_tenantId, { id, reviewerId, comment, expectedState }) {
  const data = await azurePost('/api/risks-write?action=add-reviewer', {
    id,
    reviewerId,
    comment,
    ...expectedStateField(expectedState),
  });
  return { risk: data.risk, evidence: data.evidence, alreadyOnList: !!data.alreadyOnList };
}

// ── Risk proposal lifecycle ───────────────────────────────────────────────────

export async function acceptRiskProposal(_tenantId, { proposalId }) {
  const data = await azurePost('/api/risks-write?action=accept-proposal', { proposalId });
  return data.proposal;
}

export async function dismissRiskProposal(_tenantId, { proposalId, reason }) {
  const data = await azurePost('/api/risks-write?action=dismiss-proposal', { proposalId, reason });
  return data.proposal;
}
